import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { readCollapsed, writeCollapsed } from './sidebar-collapse-storage';

/**
 * Shared expanded/collapsed state for the navigation sidebar (F-043). A trivial
 * two-state UI toggle (constitution §XI-exempt). Hosted by `AppShell` so the
 * `Sidebar`, `TopBar`, and shell all read one source of truth without
 * prop-drilling through route call-sites. Persistence is layered on in US3.
 */
export type SidebarCollapseValue = {
  /** `true` ⇒ icons-only rail; `false` ⇒ full menu (default). */
  readonly collapsed: boolean;
  /** Flip the collapsed state. */
  readonly toggle: () => void;
  /** Set the collapsed state explicitly. */
  readonly setCollapsed: (value: boolean) => void;
  /** `true` only inside a `SidebarCollapseProvider`; `false` for the safe default. */
  readonly isAvailable: boolean;
};

/**
 * Safe default returned when `useSidebarCollapse` is called with no provider
 * above it — keeps `TopBar`/`AppShell` usable outside the shell (e.g. in tests)
 * without throwing. The toggle/setter are no-ops.
 */
const SAFE_DEFAULT: SidebarCollapseValue = {
  collapsed: false,
  toggle: () => {},
  setCollapsed: () => {},
  isAvailable: false,
};

const SidebarCollapseContext = createContext<SidebarCollapseValue | null>(null);

export type SidebarCollapseProviderProps = {
  readonly children: ReactNode;
};

export function SidebarCollapseProvider({ children }: SidebarCollapseProviderProps) {
  // Seed from the persisted preference (US3); because AppShell re-mounts per route,
  // this restore-on-mount is what keeps the choice consistent across navigation.
  const [collapsed, setCollapsedState] = useState<boolean>(readCollapsed);
  const collapsedRef = useRef(collapsed);

  // Persist only on an explicit change — never on mount. A mount-effect would be
  // replayed by React StrictMode and write the seeded default, so persistence
  // lives in the change handlers instead (FR-008/FR-009/FR-014).
  const setCollapsed = useCallback((value: boolean) => {
    collapsedRef.current = value;
    setCollapsedState(value);
    writeCollapsed(value);
  }, []);
  const toggle = useCallback(() => setCollapsed(!collapsedRef.current), [setCollapsed]);

  const value = useMemo<SidebarCollapseValue>(
    () => ({ collapsed, toggle, setCollapsed, isAvailable: true }),
    [collapsed, toggle, setCollapsed],
  );
  return (
    <SidebarCollapseContext.Provider value={value}>{children}</SidebarCollapseContext.Provider>
  );
}

/** Read the shared sidebar collapse state; safe outside a provider. */
export function useSidebarCollapse(): SidebarCollapseValue {
  return useContext(SidebarCollapseContext) ?? SAFE_DEFAULT;
}
